import type {
  LoginInput,
  LoginOption,
  LoginResult,
} from '../../domain/entities/auth.entity';
import type { AuthStorageDatasource } from '../datasources/auth-storage.datasource';
import type { AuthApiDatasource } from '../datasources/auth-api.datasource';

export interface VerifyOutcome {
  token: string;
  result: LoginResult;
}

export class AuthRepositoryImpl {
  private readonly storage: AuthStorageDatasource;
  private readonly api: AuthApiDatasource;

  constructor(storage: AuthStorageDatasource, api: AuthApiDatasource) {
    this.storage = storage;
    this.api = api;
  }

  private encode(input: LoginInput): string {
    return `Basic ${btoa(`${input.email}:${input.password}`)}`;
  }

  // Verifica sem restringir a nenhuma institution — devolve todas as listas
  // que esse login acessa, em qualquer instituição, mesmo que a URL atual
  // seja de outra (ex.: entrar por /FAG mas o login também ter ARISTO).
  // Devolve sempre uma lista de opções pro usuário confirmar qual turma quer
  // acessar — mesmo quando só existe 1 (nesse caso a lista tem 1 item só).
  // Quem efetivamente salva a sessão é `selectOption`, depois do usuário clicar.
  async login(input: LoginInput): Promise<VerifyOutcome> {
    const token = this.encode(input);
    return { token, result: await this.resolveOptions(token) };
  }

  // Reaproveita um token Basic de uma sessão já válida (não pede senha de
  // novo) pra listar de novo todas as turmas do login — usado no "trocar turma".
  async reverify(token: string): Promise<VerifyOutcome> {
    return { token, result: await this.resolveOptions(token) };
  }

  private async resolveOptions(token: string): Promise<LoginResult> {
    const result = await this.api.verify(token);

    if (result.status === 'resolved') {
      return {
        status: 'ambiguous',
        options: [
          {
            institution: result.institution,
            courseId: result.courseId,
            description: result.description,
          },
        ],
      };
    }
    if (result.status === 'ambiguous') {
      return { status: 'ambiguous', options: result.options };
    }
    return { status: 'invalid' };
  }

  // Confirma a escolha de uma das opções devolvidas por `login`/`reverify`.
  async selectOption(token: string, option: LoginOption): Promise<LoginResult> {
    const result = await this.api.verify(token, option.institution, option.courseId);

    if (result.status === 'resolved') {
      this.storage.saveSession(result.institution, { token, courseId: result.courseId });
      return { status: 'success' };
    }
    return { status: 'invalid' };
  }

  getToken(institution: string): string | null {
    return this.storage.getSession(institution)?.token ?? null;
  }

  getCourseId(institution: string): string | null {
    return this.storage.getSession(institution)?.courseId ?? null;
  }

  isAuthenticated(institution: string): boolean {
    return this.storage.getSession(institution) !== null;
  }

  getEmail(institution: string): string | null {
    const session = this.storage.getSession(institution);
    if (!session) return null;
    try {
      const [email] = atob(session.token.replace(/^Basic\s+/, '')).split(':');
      return email || null;
    } catch {
      return null;
    }
  }

  clearToken(institution: string): void {
    this.storage.clearSession(institution);
  }
}
